import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatError } from "../api";
import {
  Building2, Globe2, MapPin, Phone, Mail, Linkedin, Twitter, Instagram, Youtube,
  Facebook, Image as ImageIcon, Save, Loader2, CheckCircle2, Users, Calendar,
  Sparkles, Briefcase, Tag,
} from "lucide-react";
import usePageTitle from "../hooks/usePageTitle";

const INDUSTRIES = [
  "Software / IT Services", "Internet / Product", "Semiconductor / VLSI",
  "Manufacturing", "EV / Mobility", "Healthcare / MedTech", "Robotics / Automation",
  "Cybersecurity", "Aerospace / Defense", "FinTech / BFSI", "EdTech", "AgriTech",
  "Consulting", "Energy / Cleantech", "Other",
];
const SIZE_BUCKETS = ["1-10", "11-50", "51-200", "201-500", "501-1000", "1001-5000", "5001-10000", "10000+"];

function Section({ icon: Icon, title, hint, children }) {
  return (
    <section className="card card-pad" data-testid={`co-profile-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start gap-3 mb-4">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: "rgba(0,108,255,0.10)", color: "var(--signal)" }}>
          {Icon ? <Icon size={18} /> : null}
        </span>
        <div className="min-w-0">
          <h3 className="font-display font-bold text-lg leading-tight">{title}</h3>
          {hint ? <p className="text-xs text-muted mt-0.5">{hint}</p> : null}
        </div>
      </div>
      {children}
    </section>
  );
}

function Field({ label, hint, children, full = false }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="eyebrow">{label}</label>
      <div className="mt-1.5">{children}</div>
      {hint ? <div className="text-[11px] text-muted mt-1">{hint}</div> : null}
    </div>
  );
}

function ChipInput({ items, onAdd, onRemove, placeholder, testId }) {
  const [v, setV] = useState("");
  const onKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); const t = v.trim(); if (t) { onAdd(t); setV(""); } }
  };
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2" data-testid={testId}>
        {items.map((x) => (
          <span key={x} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border" style={{ background: "rgba(153,51,255,0.08)", color: "#7B2FE0", borderColor: "rgba(153,51,255,0.25)" }}>
            {x}
            <button type="button" onClick={() => onRemove(x)} className="hover:text-red-600" aria-label="remove">×</button>
          </span>
        ))}
        {items.length === 0 && <span className="text-xs text-muted">No items yet — press Enter to add.</span>}
      </div>
      <input className="input" placeholder={placeholder} value={v} onChange={(e) => setV(e.target.value)} onKeyDown={onKey} data-testid={`${testId}-input`} />
    </div>
  );
}

export default function CompanyProfile() {
  usePageTitle("Company Profile");
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [logoBusy, setLogoBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setErr("");
    try {
      const r = await api.get("/me/company-profile");
      setForm({
        ...r.data,
        tech_stack: r.data.tech_stack || [],
        hiring_focus: r.data.hiring_focus || [],
      });
    } catch (e) { setErr(formatError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form) return;
    setBusy(true); setErr(""); setMsg("");
    try {
      const payload = {};
      [
        "organization", "about", "website", "industry", "employee_count", "founded_year",
        "headquarters", "mission", "perks_overview",
        "address_line1", "address_line2", "city", "state", "country", "postal_code",
        "phone", "public_email",
        "linkedin_url", "twitter_url", "instagram_url", "youtube_url", "facebook_url",
        "tech_stack", "hiring_focus",
      ].forEach((k) => { if (form[k] !== undefined && form[k] !== null) payload[k] = form[k]; });
      if (payload.founded_year) payload.founded_year = Number(payload.founded_year);
      const r = await api.put("/me/company-profile", payload);
      setForm({
        ...r.data,
        tech_stack: r.data.tech_stack || [],
        hiring_focus: r.data.hiring_focus || [],
      });
      setMsg("Saved");
      setTimeout(() => setMsg(""), 2400);
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/me/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, company_logo: r.data.company_logo }));
      setMsg("Logo updated");
      setTimeout(() => setMsg(""), 2400);
    } catch (e) { setErr(formatError(e)); }
    finally { setLogoBusy(false); }
  };

  const deleteLogo = async () => {
    if (!form?.company_logo) return;
    if (!window.confirm("Remove company logo?")) return;
    setLogoBusy(true); setErr("");
    try {
      await api.delete("/me/company/logo");
      setForm((f) => ({ ...f, company_logo: null }));
      setMsg("Logo removed");
      setTimeout(() => setMsg(""), 2400);
    } catch (e) { setErr(formatError(e)); }
    finally { setLogoBusy(false); }
  };

  if (!form) {
    return (
      <div className="card card-pad text-muted animate-pulse" data-testid="co-profile-loading">
        Loading company profile…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="company-profile">
      {/* Hero */}
      <div className="rounded-sm p-6 text-white bg-brand-gradient" data-testid="co-profile-hero">
        <span className="eyebrow on-grad-muted">Company profile</span>
        <div className="mt-2 flex items-center gap-4 flex-wrap">
          <div className="h-16 w-16 rounded-lg bg-white/95 flex items-center justify-center overflow-hidden shadow-md">
            {form.company_logo
              ? <img src={form.company_logo} alt="Logo" className="h-full w-full object-contain" />
              : <Building2 size={28} className="text-signal" />}
          </div>
          <div className="min-w-0">
            <h2 className="font-display font-black text-3xl leading-tight">
              {form.organization || form.name || "Your company"}
            </h2>
            <p className="on-grad-muted text-sm mt-1">
              {form.industry || "Add your industry"} · {form.headquarters || "Add a headquarters"} · {form.employee_count || "Team size"} employees
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {msg && <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-white/15 text-white"><CheckCircle2 size={14} /> {msg}</span>}
            <button onClick={save} disabled={busy} data-testid="co-profile-save"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-white text-signal font-bold text-xs uppercase tracking-wider disabled:opacity-60 hover:-translate-y-0.5 transition-all">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {busy ? "Saving…" : "Save profile"}
            </button>
          </div>
        </div>
      </div>

      {err && <div className="card card-pad text-red-600 text-sm" data-testid="co-profile-err">{err}</div>}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Basics */}
        <Section icon={Building2} title="Company basics" hint="What recruiters and candidates see first.">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Display name" full>
              <input className="input" value={form.organization || ""} onChange={(e) => update("organization", e.target.value)} data-testid="co-profile-name" />
            </Field>
            <Field label="Industry">
              <select className="input" value={form.industry || ""} onChange={(e) => update("industry", e.target.value)} data-testid="co-profile-industry">
                <option value="">Select industry</option>
                {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </Field>
            <Field label="Company size">
              <select className="input" value={form.employee_count || ""} onChange={(e) => update("employee_count", e.target.value)} data-testid="co-profile-size">
                <option value="">Select size</option>
                {SIZE_BUCKETS.map((s) => <option key={s} value={s}>{s} employees</option>)}
              </select>
            </Field>
            <Field label="Founded year">
              <input className="input" type="number" min="1850" max="2026" placeholder="2018" value={form.founded_year || ""} onChange={(e) => update("founded_year", e.target.value)} data-testid="co-profile-year" />
            </Field>
            <Field label="Headquarters">
              <input className="input" placeholder="Bengaluru, India" value={form.headquarters || ""} onChange={(e) => update("headquarters", e.target.value)} data-testid="co-profile-hq" />
            </Field>
            <Field label="About" full hint="What does your company do? Why should top talent join?">
              <textarea className="input min-h-[120px]" value={form.about || ""} onChange={(e) => update("about", e.target.value)} maxLength={2000} data-testid="co-profile-about" />
            </Field>
            <Field label="Our mission" full>
              <textarea className="input min-h-[80px]" value={form.mission || ""} onChange={(e) => update("mission", e.target.value)} maxLength={1000} data-testid="co-profile-mission" />
            </Field>
          </div>
        </Section>

        {/* Logo */}
        <Section icon={ImageIcon} title="Brand & logo" hint="Square logos render best on cards and emails.">
          <div className="flex items-center gap-5">
            <div className="h-24 w-24 rounded-xl bg-paper border border-rule flex items-center justify-center overflow-hidden">
              {form.company_logo
                ? <img src={form.company_logo} alt="Logo" className="h-full w-full object-contain" />
                : <Building2 size={28} className="text-muted" />}
            </div>
            <div className="space-y-2">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="co-profile-logo-input" />
              <div className="flex items-center gap-2 flex-wrap">
                <button className="btn-primary" onClick={() => fileRef.current?.click()} disabled={logoBusy} data-testid="co-profile-logo-btn">
                  {logoBusy ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />}
                  {logoBusy ? "Uploading…" : (form.company_logo ? "Replace logo" : "Upload logo")}
                </button>
                {form.company_logo && (
                  <button
                    type="button"
                    className="btn-ghost text-red-600"
                    onClick={deleteLogo}
                    disabled={logoBusy}
                    data-testid="co-profile-logo-delete-btn"
                  >
                    Remove
                  </button>
                )}
              </div>
              <div className="text-[11px] text-muted">PNG / JPG / WebP / SVG · ≤ 4 MB</div>
            </div>
          </div>
        </Section>

        {/* Address */}
        <Section icon={MapPin} title="Address" hint="Used on offer letters, sitemaps and the public company page.">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Address line 1" full>
              <input className="input" value={form.address_line1 || ""} onChange={(e) => update("address_line1", e.target.value)} data-testid="co-profile-addr1" />
            </Field>
            <Field label="Address line 2" full>
              <input className="input" value={form.address_line2 || ""} onChange={(e) => update("address_line2", e.target.value)} data-testid="co-profile-addr2" />
            </Field>
            <Field label="City">
              <input className="input" value={form.city || ""} onChange={(e) => update("city", e.target.value)} data-testid="co-profile-city" />
            </Field>
            <Field label="State / Region">
              <input className="input" value={form.state || ""} onChange={(e) => update("state", e.target.value)} data-testid="co-profile-state" />
            </Field>
            <Field label="Country">
              <input className="input" value={form.country || "India"} onChange={(e) => update("country", e.target.value)} data-testid="co-profile-country" />
            </Field>
            <Field label="Postal code">
              <input className="input" value={form.postal_code || ""} onChange={(e) => update("postal_code", e.target.value)} data-testid="co-profile-zip" />
            </Field>
          </div>
        </Section>

        {/* Contact */}
        <Section icon={Phone} title="Contact" hint="Public-facing — visible to candidates on the company page.">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Public phone">
              <input className="input" value={form.phone || ""} onChange={(e) => update("phone", e.target.value)} placeholder="+91 80 1234 5678" data-testid="co-profile-phone" />
            </Field>
            <Field label="Public email">
              <input className="input" type="email" value={form.public_email || ""} onChange={(e) => update("public_email", e.target.value)} placeholder="careers@company.com" data-testid="co-profile-email" />
            </Field>
            <Field label="Website" full>
              <input className="input" value={form.website || ""} onChange={(e) => update("website", e.target.value)} placeholder="https://" data-testid="co-profile-web" />
            </Field>
          </div>
        </Section>

        {/* Social */}
        <Section icon={Globe2} title="Social presence" hint="Help candidates verify your brand.">
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { k: "linkedin_url", Icon: Linkedin, label: "LinkedIn" },
              { k: "twitter_url",  Icon: Twitter,  label: "X / Twitter" },
              { k: "instagram_url",Icon: Instagram,label: "Instagram" },
              { k: "youtube_url",  Icon: Youtube,  label: "YouTube" },
              { k: "facebook_url", Icon: Facebook, label: "Facebook" },
            ].map(({ k, Icon, label }) => (
              <Field key={k} label={label}>
                <div className="relative">
                  <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                  <input className="input input-with-icon" value={form[k] || ""} onChange={(e) => update(k, e.target.value)} data-testid={`co-profile-${k}`} placeholder="https://" />
                </div>
              </Field>
            ))}
          </div>
        </Section>

        {/* Hiring focus + Tech */}
        <Section icon={Sparkles} title="Hiring focus & tech stack" hint="Power recommendations and student suggestions.">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Tech stack" full>
              <ChipInput
                items={form.tech_stack || []}
                onAdd={(x) => update("tech_stack", [...new Set([...(form.tech_stack || []), x])])}
                onRemove={(x) => update("tech_stack", (form.tech_stack || []).filter((y) => y !== x))}
                placeholder="React, Python, AWS… (Enter)"
                testId="co-profile-tech"
              />
            </Field>
            <Field label="Hiring focus" full>
              <ChipInput
                items={form.hiring_focus || []}
                onAdd={(x) => update("hiring_focus", [...new Set([...(form.hiring_focus || []), x])])}
                onRemove={(x) => update("hiring_focus", (form.hiring_focus || []).filter((y) => y !== x))}
                placeholder="VLSI, Embedded, Cloud SRE… (Enter)"
                testId="co-profile-focus"
              />
            </Field>
            <Field label="Perks & culture" full>
              <textarea className="input min-h-[80px]" value={form.perks_overview || ""} onChange={(e) => update("perks_overview", e.target.value)} maxLength={1000} placeholder="ESOPs, hybrid, learning budget…" data-testid="co-profile-perks" />
            </Field>
          </div>
        </Section>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={busy} className="btn-primary" data-testid="co-profile-save-bottom">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {busy ? "Saving…" : "Save changes"}
        </button>
      </div>

      {/* Quick stats footer */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { Icon: Briefcase, label: "Industry", v: form.industry || "—" },
          { Icon: Users,     label: "Size",     v: form.employee_count || "—" },
          { Icon: Calendar,  label: "Founded",  v: form.founded_year || "—" },
          { Icon: Tag,       label: "Tech",     v: (form.tech_stack || []).length || "—" },
        ].map(({ Icon, label, v }) => (
          <div key={label} className="card card-pad flex items-center gap-3">
            <Icon size={18} className="text-signal" />
            <div className="min-w-0">
              <div className="text-[11px] text-muted uppercase tracking-wider">{label}</div>
              <div className="font-semibold truncate">{v}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
